import { FirebaseApp, deleteApp, initializeApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import { Auth, User, UserInfo, UserMetadata, getAuth } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';

// Test-only Firebase config. Values don't matter — unit tests never make a
// network request. The only constraint is shape: initializeApp() validates
// that required fields are strings.
const TEST_APP_OPTIONS = {
  apiKey: 'fake-api-key',
  authDomain: 'fake.firebaseapp.com',
  projectId: 'fake-project',
  appId: '1:000000000000:web:fake0000000000000',
  messagingSenderId: '000000000000',
  measurementId: 'G-FAKE0000',
};

let counter = 0;

export interface FirebaseTestHandles {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  analytics: Analytics;
  teardown: () => Promise<void>;
}

/**
 * Spins up a uniquely-named Firebase app for a single test, plus structurally-
 * typed Auth/Firestore/Analytics handles bound to it. Call `teardown()` in
 * `afterEach` to release the app — `initializeApp` rejects duplicate names.
 *
 * Replaces hand-rolled `{} as Auth` / `as unknown as Firestore` mocks. Mocks
 * only exercise what the spec author remembered to stub; real handles
 * surface SDK shape changes immediately.
 */
export function setupFirebaseTest(): FirebaseTestHandles {
  const name = `fido-test-${counter++}-${Date.now()}`;
  const app = initializeApp(TEST_APP_OPTIONS, name);
  const auth = getAuth(app);
  const firestore = getFirestore(app);

  // Analytics is structurally `{ app: FirebaseApp }`. Construct directly
  // rather than calling getAnalytics(), which would try to load gtag.js
  // from the network.
  const analytics: Analytics = { app };

  return {
    app,
    auth,
    firestore,
    analytics,
    teardown: () => deleteApp(app),
  };
}

const TEST_USER_INFO: UserInfo = {
  displayName: null,
  email: null,
  phoneNumber: null,
  photoURL: null,
  providerId: 'firebase',
  uid: 'test-user-uid',
};

const TEST_USER_METADATA: UserMetadata = {
  creationTime: undefined,
  lastSignInTime: undefined,
};

/**
 * Builds a structurally-complete `User` object for tests. Every method and
 * field on the SDK's `User` interface is supplied with a sensible default;
 * callers override with `{ uid: 'specific-id', emailVerified: true }` etc.
 */
export function makeUser(overrides: Partial<User> = {}): User {
  return {
    ...TEST_USER_INFO,
    emailVerified: false,
    isAnonymous: false,
    metadata: TEST_USER_METADATA,
    providerData: [],
    refreshToken: '',
    tenantId: null,
    delete: () => Promise.resolve(),
    getIdToken: () => Promise.resolve('test-token'),
    getIdTokenResult: () =>
      Promise.resolve({
        authTime: '',
        claims: {},
        expirationTime: '',
        issuedAtTime: '',
        signInProvider: null,
        signInSecondFactor: null,
        token: 'test-token',
      }),
    reload: () => Promise.resolve(),
    toJSON: () => ({}),
    ...overrides,
  };
}
